@file:OptIn(ExperimentalFoundationApi::class)

package ui.license.detail


import androidx.compose.foundation.*
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.unit.dp
import api.CurrencyType
import api.LicenseState
import compose.assets.Svg
import compose.assets.svg.Plus
import kotlinx.coroutines.launch
import ssoft.frontend.generated.resources.*
import tk.mallumo.compose.navigation.ComposableNavNode
import tk.mallumo.utils.DataState
import tools.await
import tools.formatFromRes
import tools.rememberString
import tools.stringRes
import ui.license.detail.components.LicenseUploadDialog
import ui.snackBarStateGlobal
import ui.widget.*


data class LicenseArgs(var companyId: Long = -1)

@ComposableNavNode(LicenseArgs::class)
@Composable
fun LicenseDetailUI() {
    with(LicenseDetailScope.rememberScope()) {
        when (val state = vm.state) {
            is DataState.Error -> ErrUI()
            is DataState.Idle,
            is DataState.Loading -> LoadingUI()

            is DataState.Result -> Content(state.nnvl)
        }
    }
}

/*
- item dialog detail
- item download
- admin dialog confirm  / decline + msg
- user can delete if in review multiple
- user can upload multiple
 */
@Composable
fun LicenseDetailScope.Content(nnvl: LicenseDetailScope.Wrapper) {
    val uploaderState = DialogState.remember(key = "uploader")

    LicenseUploadDialog(uploaderState)

    Column(Modifier.fillMaxSize()) {
        Toolbar(
            nav = nav,
            titlePrefix = Res.string.license_detail__title.rememberString(),
            titleSuffix = "${nnvl.licenses.name} (${args.companyId})"
        )
        Space(8.dp)
        val stateHorizontal = rememberScrollState(0)
        val itemMeasurer = LicenseDetailScope.headers.rememberMeasurer(16.dp, padding = 16.dp)

        Box(Modifier.fillMaxSize()) {
            if (nnvl.state.isEmpty()) {
                InitialScreen(nnvl, uploaderState)
            } else {
                Box(
                    Modifier.fillMaxSize()
                        .tableMeasurer(itemMeasurer)
                        .horizontalScroll(stateHorizontal)
                ) {
                    LazyColumn(
                        contentPadding = PaddingValues(horizontal = 8.dp),
                        modifier = Modifier.fillMaxHeight()
                            .width(itemMeasurer.rowWidth)
                    ) {
                        if (nnvl.state.isNotEmpty()) {
                            stickyHeader {
                                ListingHeader(LicenseDetailScope.headers)
                            }
                            items(nnvl.state.sortedByDescending { it.data.id }) { item ->
                                ListingDivider()
                                val state = DialogState.remember(key = "item${item.data.id}")
                                ListingItemDialog(state, nnvl, item)
                                ListingItem(
                                    item = item,
                                    specs = LicenseDetailScope.headers,
                                    background = if (login.server.id == item.data.id) MaterialTheme.colorScheme.tertiaryContainer
                                    else MaterialTheme.colorScheme.surfaceContainer
                                ) { state.show() }
                            }
                        }


                        ListingFooter()
                    }
                }
                HorizontalScrollbar(
                    modifier = Modifier
                        .align(Alignment.BottomStart)
                        .padding(bottom = 8.dp)
                        .fillMaxWidth(),
                    adapter = rememberScrollbarAdapter(stateHorizontal)
                )
            }

            if (nnvl.canAdd && nnvl.state.isNotEmpty()) {
                val scope = rememberCoroutineScope()
                FloatingActionButton(
                    onClick = {
                        if (nnvl.state.none { LicenseState.REVIEW match it.data.state }) {
                            uploaderState.show()
                        } else {
                            scope.launch {
                                snackBarStateGlobal.showSnackbar(
                                    duration = SnackbarDuration.Long,
                                    withDismissAction = true,
                                    message = Res.string.license_detail_add_validation.await(),
                                )
                            }
                        }
                    },
                    modifier = Modifier.align(Alignment.BottomEnd)
                        .padding(24.dp)
                ) {
                    Icon(Svg.Plus, null)
                }
            }
        }
    }
}

@Composable
fun LicenseDetailScope.ListingItemDialog(state: DialogState, nnvl: LicenseDetailScope.Wrapper, item: LicenseDetailScope.Wrapper.Item) {
    val msg = remember {
        mutableStateOf(TextFieldValue())
    }
    val scope = rememberCoroutineScope()

    fun sendMsg(isAccepted: Boolean): Boolean {
        val message = msg.value.text.trim()
        return if (message.isEmpty() && !isAccepted) {
            scope.launch {
                snackBarStateGlobal.showSnackbar(
                    duration = SnackbarDuration.Short,
                    withDismissAction = true,
                    message = Res.string.license_detail_item_label_message_input_validation.await(),
                )
            }
            false
        } else {
            action(
                LicenseDetailVM.Action.Confirmation(
                    id = item.data.id,
                    accept = isAccepted,
                    message = message
                )
            )
            true
        }
    }

    Dialog(state) {
        DialogUI(
            modifier = Modifier.align(Alignment.Center),
            onDismissRequest = { state.hide() },
            title = {
                Text(Res.string.license_detail_item_title.rememberString(item.data.price, CurrencyType[nnvl.licenses.currency].symbol))
            },
            dismissButton = {
                TextButton({ state.hide() }) {
                    Text(Res.string.dialog__action_cancel.rememberString())
                }
            },
            confirmButton = {
                TextButton({ action(LicenseDetailVM.Action.Download(item.data.doc)) }) {
                    Text(Res.string.license_detail_item_action_download.rememberString())
                }
            }
        ) {
            Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                Space(4.dp)
                Row {
                    Text(
                        text = Res.string.license_detail_item_label_state.rememberString(),
                        style = MaterialTheme.typography.labelLarge,
                        modifier = Modifier.width(128.dp)
                    )
                    Text(LicenseState[item.data.state].stringRes().rememberString())
                }

                if (item.data.msg.isNotEmpty()) {
                    Row {
                        Text(
                            text = Res.string.license_detail_item_label_message.rememberString(),
                            style = MaterialTheme.typography.labelLarge,
                            modifier = Modifier.width(128.dp)
                        )
                        Text(item.data.msg)
                    }
                }

                when (LicenseState[item.data.state]) {
                    LicenseState.IDLE,
                    LicenseState.TERMINATED,
                    LicenseState.DECLINED -> Unit

                    LicenseState.APPROVED -> Row {
                        Text(
                            text = Res.string.license_detail_item_label_accepted.rememberString(),
                            style = MaterialTheme.typography.labelLarge,
                            modifier = Modifier.width(128.dp)
                        )
                        Text(item.data.start.formatFromRes())
                    }

                    LicenseState.REVIEW -> {
                        if (nnvl.canEdit) {
                            Space(8.dp)
                            TextField(
                                label = { Text(Res.string.license_detail_item_label_message_input.rememberString()) },
                                value = msg.value,
                                isError = msg.value.text.trim().isEmpty(),
                                modifier = Modifier.fillMaxWidth(),
                                onValueChange = { msg.value = it })

                            Row(horizontalArrangement = Arrangement.spacedBy(16.dp)) {
                                Button(
                                    onClick = {
                                        if (sendMsg(false)) {
                                            state.hide()
                                        }
                                    },
                                    colors = ButtonDefaults.buttonColors(
                                        containerColor = MaterialTheme.colorScheme.errorContainer,
                                        contentColor = MaterialTheme.colorScheme.onErrorContainer,
                                    ),
                                    modifier = Modifier.weight(1F)
                                ) {
                                    Text(Res.string.license_detail_item_action_decline.rememberString())
                                }
                                Button(
                                    onClick = {
                                        if (sendMsg(true)) {
                                            state.hide()
                                        }
                                    },
                                    modifier = Modifier.weight(1F)
                                ) {
                                    Text(Res.string.license_detail_item_action_approve.rememberString())
                                }
                            }
                        } else if (nnvl.canTerminate) {
                            Button(
                                onClick = {
                                    action(LicenseDetailVM.Action.Terminate(item.data.id))
                                    state.hide()
                                },
                                colors = ButtonDefaults.buttonColors(
                                    containerColor = MaterialTheme.colorScheme.errorContainer,
                                    contentColor = MaterialTheme.colorScheme.onErrorContainer,
                                ),
                                modifier = Modifier.fillMaxWidth()
                            ) {
                                Text(Res.string.license_detail_item_action_terminate_review.rememberString())
                            }
                        }
                    }

                }

            }
        }
    }
}

@Suppress("UnusedReceiverParameter")
@Composable
fun LicenseDetailScope.InitialScreen(nnvl: LicenseDetailScope.Wrapper, uploaderState: DialogState) {
    Box(Modifier.fillMaxSize()) {
        if (nnvl.canAdd) {
            Button(
                onClick = { uploaderState.show() },
                modifier = Modifier.align(Alignment.Center)
            ) {
                Text(Res.string.license_detail_license_add.rememberString())
            }
        } else {
            Text(
                text = Res.string.license_detail_empty_no_permission.rememberString(),
                modifier = Modifier.align(Alignment.Center)
            )
        }
    }
}
